import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Hammer, Trophy, TrendingUp, Calendar, CheckCircle, XCircle, 
  Clock, DollarSign, Target, Flame, ChevronDown, ChevronUp 
} from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "../App";
import Navbar, { AuthModal } from "../components/Navbar";
import TeamMatchup from "../components/TeamMatchup";
import WinShareCard from "../components/WinShareCard";
import axios from "axios";
import SEO from "../components/SEO";

// Stat Card Component
const StatCard = ({ label, value, subValue, icon: Icon, color = "text-white" }) => (
  <div className="bg-hsl-surface/50 border border-white/5 rounded-xl p-4 text-center">
    {Icon && <Icon className={`w-5 h-5 mx-auto mb-2 ${color}`} />}
    <p className="text-zinc-500 text-xs uppercase tracking-wide mb-1">{label}</p>
    <p className={`font-mono text-2xl font-bold ${color}`}>{value}</p>
    {subValue && <p className="text-zinc-500 text-xs mt-1">{subValue}</p>}
  </div>
);

// Period Stats Component
const PeriodStats = ({ label, stats }) => {
  if (!stats || stats.total === 0) return null;
  
  const profit = stats.profit;
  const profitDisplay = profit >= 0 ? `+$${profit.toLocaleString()}` : `-$${Math.abs(profit).toLocaleString()}`;
  
  return (
    <div className="bg-hsl-surface/30 border border-white/5 rounded-lg p-3 text-center">
      <p className="text-zinc-500 text-xs uppercase tracking-wide mb-1">{label}</p>
      <p className="font-mono text-xl font-bold text-white">
        {stats.wins}-{stats.losses}
      </p>
      <p className={`font-mono text-sm font-semibold ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
        {profitDisplay}
      </p>
      <p className="text-zinc-600 text-xs mt-1">{stats.win_rate}% Win Rate</p>
    </div>
  );
};

// Hammer Pick Card Component
const HammerPickCard = ({ pick, onShare }) => {
  const [expanded, setExpanded] = useState(false);
  
  const resultColor = pick.result === 'win' ? 'text-green-400 border-green-500/30 bg-green-500/10' 
    : pick.result === 'loss' ? 'text-red-400 border-red-500/30 bg-red-500/10'
    : 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10';
  
  // Calculate profit based on American odds
  const calculateProfit = () => {
    if (pick.result !== 'win' && pick.result !== 'loss') return 0;
    
    const betAmount = pick.bet_amount || 100;
    const oddsNum = parseInt(String(pick.odds).replace('+', ''));
    
    if (pick.result === 'win') {
      if (oddsNum > 0) {
        return betAmount * (oddsNum / 100);
      } else {
        return betAmount * (100 / Math.abs(oddsNum));
      }
    }
    return -betAmount;
  };
  
  const profit = calculateProfit();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-hsl-surface to-zinc-900 border border-yellow-500/30 rounded-2xl overflow-hidden shadow-lg shadow-yellow-500/5"
    >
      {/* Pick Image if available */}
      {pick.pick_image_url && (
        <div className="aspect-video w-full overflow-hidden">
          <img 
            src={pick.pick_image_url.startsWith('http') ? pick.pick_image_url : `${window.location.origin}${pick.pick_image_url}`}
            alt={pick.matchup}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🔨</span>
            <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs font-black uppercase px-3 py-1 rounded-full">
              Full Hammer
            </span>
            <span className={`text-xs font-bold uppercase px-2 py-1 rounded border ${resultColor}`}>
              {pick.result === 'pending' ? 'PENDING' : pick.result.toUpperCase()}
            </span>
          </div>
          <div className="text-right">
            <p className="text-zinc-500 text-xs">{pick.sport}</p>
            <p className="text-zinc-400 text-xs flex items-center gap-1 justify-end">
              <Calendar className="w-3 h-3" />
              {new Date(pick.game_time).toLocaleDateString('en-US', { 
                timeZone: 'America/New_York',
                month: 'short', 
                day: 'numeric',
                year: 'numeric'
              })}
            </p>
          </div>
        </div>
        
        {/* Matchup */}
        <h3 className="font-heading text-xl font-bold text-white uppercase tracking-tight mb-2">
          <TeamMatchup matchup={pick.matchup} sport={pick.sport} />
        </h3>
        
        {/* Pick & Odds */}
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-cyan-500/20 border border-cyan-500/30 rounded-lg px-4 py-2">
            <p className="text-cyan-400 font-bold text-lg">{pick.pick}</p>
          </div>
          <div className="text-zinc-400">
            <span className="font-mono">{pick.odds}</span>
          </div>
          <div className="ml-auto">
            <p className="text-zinc-500 text-xs">Wager</p>
            <p className="font-mono font-bold text-white">${pick.bet_amount.toLocaleString()}</p>
          </div>
        </div>
        
        {/* Profit/Loss for completed picks */}
        {pick.result !== 'pending' && (
          <div className={`rounded-lg p-3 mb-4 ${pick.result === 'win' ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 text-sm">Result</span>
              <span className={`font-mono text-xl font-bold ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {profit >= 0 ? '+' : ''}${profit.toLocaleString()}
              </span>
            </div>
            {/* Score if available */}
            {(pick.away_score !== null && pick.home_score !== null) && (
              <p className="text-zinc-500 text-xs mt-1">
                Final: <span className="text-white font-mono">{pick.away_score} - {pick.home_score}</span>
              </p>
            )}
          </div>
        )}
        
        {/* Analysis - Expandable */}
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between text-zinc-400 hover:text-white transition-colors py-2"
          >
            <span className="text-sm font-medium">Analysis</span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="bg-hsl-bg/50 rounded-lg p-4 border border-white/5">
                  <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">
                    {pick.analysis}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Share Button for Wins */}
        {pick.result === 'win' && (
          <div className="mt-4 pt-4 border-t border-white/5">
            <Button
              onClick={() => onShare(pick)}
              variant="outline"
              className="w-full bg-cyan-500/10 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
            >
              Share This Win
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const HammerCollectionPage = () => {
  const [picks, setPicks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [sharePick, setSharePick] = useState(null);
  const [filter, setFilter] = useState("all"); // "all", "wins", "losses", "pending"

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [picksRes, statsRes] = await Promise.all([
          axios.get(`${API}/picks/hammer`),
          axios.get(`${API}/stats/hammer`)
        ]);
        setPicks(picksRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error("Failed to fetch hammer data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredPicks = picks.filter(p => {
    if (filter === "all") return true;
    if (filter === "wins") return p.result === "win";
    if (filter === "losses") return p.result === "loss";
    if (filter === "pending") return p.result === "pending";
    return true;
  });

  return (
    <div className="min-h-screen bg-hsl-bg" data-testid="hammer-collection-page">
      <SEO 
        title="The Hammer Collection | HSL"
        description="Our premium Hammer picks - the highest conviction plays with full analysis and results."
      />
      <Navbar 
        onLoginClick={() => { setAuthMode("login"); setAuthModalOpen(true); }}
        onSignupClick={() => { setAuthMode("signup"); setAuthModalOpen(true); }}
      />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="text-5xl">🔨</span>
            <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tight">
              <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                The Hammer Collection
              </span>
            </h1>
            <span className="text-5xl">🔨</span>
          </div>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Our highest conviction plays. When we swing the hammer, we mean business. 
            These are the picks we believe in most - backed by extensive analysis and maximum confidence.
          </p>
        </motion.div>

        {/* Stats Section */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-10"
          >
            {/* Main Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard 
                label="All-Time Record"
                value={`${stats.all_time.wins}-${stats.all_time.losses}`}
                subValue={`${stats.all_time.win_rate}% Win Rate`}
                icon={Trophy}
                color="text-yellow-400"
              />
              <StatCard 
                label="All-Time Profit"
                value={stats.all_time.profit >= 0 ? `+$${stats.all_time.profit.toLocaleString()}` : `-$${Math.abs(stats.all_time.profit).toLocaleString()}`}
                icon={DollarSign}
                color={stats.all_time.profit >= 0 ? "text-green-400" : "text-red-400"}
              />
              <StatCard 
                label="Current Streak"
                value={`${stats.current_streak}${stats.streak_type === 'win' ? 'W' : stats.streak_type === 'loss' ? 'L' : ''}`}
                icon={stats.is_hot ? Flame : TrendingUp}
                color={stats.streak_type === 'win' ? "text-green-400" : stats.streak_type === 'loss' ? "text-red-400" : "text-zinc-400"}
              />
              <StatCard 
                label="Total Hammers"
                value={stats.all_time.total + stats.all_time.pending}
                subValue={stats.all_time.pending > 0 ? `${stats.all_time.pending} pending` : null}
                icon={Target}
                color="text-cyan-400"
              />
            </div>
            
            {/* Period Breakdowns */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <PeriodStats label="Last 7 Days" stats={stats.last_7_days} />
              <PeriodStats label="Last 30 Days" stats={stats.last_30_days} />
              <PeriodStats label="All Time" stats={stats.all_time} />
            </div>
          </motion.div>
        )}

        {/* Filter Tabs */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[
            { key: "all", label: "All Picks" },
            { key: "wins", label: "Wins" },
            { key: "losses", label: "Losses" },
            { key: "pending", label: "Pending" }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === tab.key
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'bg-hsl-surface/50 text-zinc-400 border border-white/5 hover:text-white'
              }`}
            >
              {tab.label}
              {tab.key !== "all" && (
                <span className="ml-2 text-xs opacity-70">
                  ({picks.filter(p => 
                    tab.key === "wins" ? p.result === "win" :
                    tab.key === "losses" ? p.result === "loss" :
                    p.result === "pending"
                  ).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Picks Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-hsl-surface/50 rounded-2xl h-96 animate-pulse" />
            ))}
          </div>
        ) : filteredPicks.length === 0 ? (
          <div className="text-center py-16">
            <span className="text-6xl mb-4 block">🔨</span>
            <p className="text-zinc-500 text-lg">No Hammer picks found</p>
            <p className="text-zinc-600 text-sm mt-2">
              {filter !== "all" ? "Try a different filter" : "Check back soon for our next big play"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredPicks.map((pick) => (
              <HammerPickCard 
                key={pick.id} 
                pick={pick}
                onShare={setSharePick}
              />
            ))}
          </div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-zinc-500 mb-4">Want to see all our picks?</p>
          <Link to="/dashboard">
            <Button className="btn-primary-glow px-8 py-3">
              View All Picks
            </Button>
          </Link>
        </motion.div>
      </main>

      {/* Share Win Modal */}
      {sharePick && (
        <WinShareCard pick={sharePick} onClose={() => setSharePick(null)} />
      )}

      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
        setMode={setAuthMode}
      />
    </div>
  );
};

export default HammerCollectionPage;
